import React from 'react';
import { Button, IconButton } from '@parsina/framework';
import { Mic, Square, Loader2, AlertTriangle } from 'lucide-react';
import { C, soft } from '../../constants/theme';
import useSpeechToText, { dictationSupported } from '../../hooks/useSpeechToText.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import CoinChip from './CoinChip.jsx';
import { useCreditPrices } from '../../lib/useCreditPrices.js';

// Microphone affordance for a text field: press, speak, the words land in the
// field. See useSpeechToText.js for which engine runs the recognition.
//
// ── 2026-08-26: THE OVERLAY IS GONE, AND THE WORDS GO IN THE FIELD LIVE ────
// Dictation used to open a modal for the duration of the recording: a pulsing
// microphone, a hint, a «پایان» button. Two things were wrong with it, and they
// are the same thing said twice.
//
//   * IT COVERED THE WORK. In «دستیار شرح حال» the dentist dictates INTO a
//     visible textarea and then presses «تکمیل…»; a dialog over that form hides
//     both, so the one thing they want to watch — the narrative filling up — was
//     behind the window reporting that it was being filled.
//   * IT REPORTED NOTHING THE FIELD COULD NOT. The modal's own comment admitted
//     its transcript box was almost always empty and had to be replaced by a
//     decorative pulse. What it was standing in for is the field itself.
//
// So the live session now reports itself INLINE, beside the microphone, in the
// same slot the post-recording wait and any failure already used:
//
//     [◉] ● در حال شنیدن ۰۰:۱۲
//
// and the interim text goes to the caller through `onInterim` as it arrives, so
// the field fills while the dentist is still speaking.
//
// ── ON THE SERVER ENGINE THERE IS NO LONGER ANY INTERIM TEXT ──────────────
// `onInterim` is still called, always with ''. The service's interim frames
// promise to carry only the new words and do not keep that promise — they
// re-send the whole transcript whenever its re-transcription of a longer buffer
// stops matching the previous one character for character — so accumulating
// them repeated whole phrases back at the dentist. useServerSpeechToText.js
// carries the diagnosis; the browser engine's preview is sound and unaffected.
// The inline pulse and clock below are the live feedback in the meantime, which
// they already were for the first ten seconds of every session.
//
// ── THE TIMER IS NOT DECORATION, IT IS THE PRICE ──────────────────────────
// The server engine bills 5 پارسه per minute, ROUNDED UP by the service. With
// no clock on screen the dentist had no way to tell a 50-second dictation from
// a 70-second one — one minute's charge from two. The elapsed time is now the
// one number that says what this session is going to cost.
//
// ── THE TWO ENGINES STILL DIFFER, AND THE BUTTON STILL HAS TO SAY SO ──────
//   * THE BROWSER ENGINE CLOSES ITSELF at a natural pause; the server engine
//     records until the dentist presses stop. That is why the microphone turns
//     into a STOP square while live — with the modal gone it is the only stop
//     control there is, so it has to look like one.
//   * SHORT DICTATIONS SHOW NO INTERIM TEXT on the server engine: the service
//     transcribes on a timer (~10s to the first hint), so most of شکایت اصلی
//     produces nothing until the final transcript. The pulsing dot and the
//     running clock are the whole feedback there, which is why they are never
//     conditional on words having arrived.
//   * IT COSTS پارسه, where the browser engine is free, so the price chip shows
//     on one and not the other.
//
// ── TWO APPEARANCES, ONE BEHAVIOUR ────────────────────────────────────────
// `appearance="icon"` (default) is the 44px microphone beside a field —
// dictation as an accessory to typing. `appearance="wide"` is a full labelled
// button, for the one place dictation is not an accessory but the ONLY input:
// «دستیار شرح حال», where the typed narrative box was removed and speaking IS
// the form. There the control has to carry its own explanation, and its stop
// state has to say what stopping causes (`stopLabel` — «پایان و دریافت
// توصیه»), because the button that used to make that promise is gone.
//
// ── WHAT A CALLER OWES `onInterim` ────────────────────────────────────────
// Interim text is a PREVIEW and is later SUPERSEDED — the server engine's final
// message carries the whole session, not the tail of it. A caller must hold it
// beside the committed value and show the concatenation, never merge it in:
// appending both would write everything twice. See SoapAssistant for the shape.
export default function VoiceDictateButton({
  onText,
  // Live, not-yet-final words for the current session, or '' when there is no
  // session. OPTIONAL: a caller that omits it simply gets the words at the end,
  // which is how this component behaved before.
  onInterim,
  disabled = false,
  label,
  // 'icon' | 'wide' — see the appearance note above.
  appearance = 'icon',
  // Wide appearance only: what the live button says instead of the generic
  // «پایان ضبط», for a caller whose stop has a consequence worth naming.
  stopLabel,
  size = 44,
  // Pass for a multi-line field, where a pause between sentences is thinking
  // rather than the end of the answer. Honoured by the browser engine; the
  // server engine records until stopped either way. See useSpeechToText.
  continuous = false,
}) {
  const { t, locale, formatDigits } = useTranslation();
  // The policy's per-minute price once the list has arrived; the constant until then.
  const { costOf } = useCreditPrices();

  // Appends rather than replaces: dictation is a second way to fill the field,
  // not a reset of what the dentist already typed.
  const handleResult = React.useCallback(
    (text) => {
      if (text) onText(text);
    },
    [onText],
  );

  const { listening, transcribing, interim, error, engine, start, stop } = useSpeechToText({
    locale,
    continuous,
    onResult: handleResult,
  });

  // ── The interim stream, forwarded to the field ────────────────────────────
  // Read through a ref so a caller may pass a fresh closure every render — which
  // it will, since the handler closes over the field's value — without this
  // effect re-firing and re-sending the same preview.
  //
  // `listening || transcribing`, not `listening` alone: the hook keeps the
  // interim text through the post-recording wait, and dropping the preview at
  // «پایان» would blank the field for the seconds before the final transcript
  // lands. Teardown clears both flags AND the interim in the same batch as the
  // final result, so the preview disappears exactly as the real words arrive —
  // never a frame showing both.
  //
  // A LAYOUT effect, not a passive one, and only for the last render of a
  // session: the final transcript reaches the caller through `onText` during the
  // socket's message handler, while the preview is cleared from here. A passive
  // effect runs AFTER the paint, so for one frame the field would show the
  // committed words followed by the preview of the same words — the duplication
  // this whole contract exists to avoid, visible just long enough to be noticed.
  const liveRef = React.useRef(onInterim);
  React.useEffect(() => { liveRef.current = onInterim; }, [onInterim]);
  React.useLayoutEffect(() => {
    liveRef.current?.((listening || transcribing) ? interim : '');
  }, [interim, listening, transcribing]);

  // ── The clock ─────────────────────────────────────────────────────────────
  // Restarted from zero by `listening` going true, and left frozen at its last
  // value when it goes false — the interval stops, the state does not reset,
  // because the chip unmounts with it and a reset would be a visible flicker to
  // «۰۰:۰۰» on the way out.
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    if (!listening) return undefined;
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [listening]);

  const begin = () => start();

  // Hidden only where NEITHER engine can work — a browser with no recogniser
  // and no MediaRecorder, or an insecure origin where getUserMedia does not
  // exist. A disabled-but-visible mic reads as "broken today, retry later",
  // which is the wrong promise for something that will never work here.
  if (!dictationSupported()) return null;

  // The label IS the affordance now that the modal's «پایان» is gone: while a
  // session is live this button is the stop control, and its tooltip and
  // accessible name have to say so.
  const title = listening ? t('common.voice.stop') : (label || t('common.voice.start'));

  // Only the server engine reaches the AI service, and only the AI service
  // charges. Advertising a price on the free path would be a straight untruth.
  const priced = engine === 'server';

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {appearance === 'wide' ? (
          <Button
            // Destructive red while live — the same colour as the dot beside
            // it, so the two read as one state rather than a button and a
            // warning. The kit's destructive variant IS the recording red.
            variant={listening ? 'destructive' : 'default'}
            className="min-h-11"
            // Inert while the words are being worked out: there is nothing to
            // stop and nothing to start, and a second press would only re-ask
            // the server for a transcript it is already producing.
            disabled={disabled || transcribing}
            aria-pressed={listening}
            data-testid="voice-mic"
            onClick={listening ? stop : begin}
          >
            {listening
              ? <Square size={13} aria-hidden fill="currentColor" />
              : <Mic size={15} aria-hidden />}
            {listening ? (stopLabel || t('common.voice.stop')) : title}
          </Button>
        ) : (
        <IconButton
          // Red and filled while live — the same colour as the dot beside it,
          // so the two read as one state rather than a button and a warning.
          variant={listening ? 'default' : 'ghost'}
          size={size}
          // Inert while the words are being worked out: there is nothing to stop
          // and nothing to start, and a second press would only re-ask the server
          // for a transcript it is already producing.
          disabled={disabled || transcribing}
          label={title}
          aria-pressed={listening}
          data-testid="voice-mic"
          onClick={listening ? stop : begin}
          style={listening ? { background: C.red, color: '#fff' } : undefined}
          icon={listening
            ? <Square size={14} aria-hidden fill="currentColor" />
            : <Mic size={16} aria-hidden />}
        />
        )}
        {/* The price is hidden for every state that has something more urgent to
            say in this row: while recording the clock is the price, and while
            transcribing the wait is. */}
        {priced && !disabled && !listening && !transcribing && (
          <CoinChip
            cost={costOf('TRANSCRIPTION_PER_MIN')}
            per={t('common.units.minute')}
            tone="mute"
            title={t('common.voice.priceTitle')}
            testid="voice-cost"
          />
        )}
      </span>

      {/* ── THE LIVE SESSION ──────────────────────────────────────────────
          What the overlay used to be, in one line beside the microphone: a
          pulsing dot that says the session is open, the words «در حال شنیدن»,
          and the elapsed time.

          THE DOT IS DECORATIVE — a CSS animation, not the audio level. It must
          never be read as "your voice is registering"; it says the session is
          live and nothing more. A meter driven by the microphone would be a
          promise this has no signal to keep.

          `nowrap` and `flexShrink: 0`: in شکایت اصلی's row this sits beside a
          flexed TextInput that the interim words are landing in, and a chip
          that wrapped mid-clock — or a field squeezed to nothing — would cost
          more than the ~120px it takes. */}
      {listening && (
        <span
          role="status"
          aria-live="polite"
          data-testid="voice-live"
          data-phase="listening"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: C.red, whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'relative', width: 8, height: 8, borderRadius: '50%',
              background: C.red, flexShrink: 0,
            }}
          >
            <span
              className="animate-ping"
              style={{
                position: 'absolute', inset: -2, borderRadius: '50%',
                background: soft(C.red, 0.5),
              }}
            />
          </span>
          {t('common.voice.listening')}
          {/* Tabular figures so the clock does not jitter sideways once a
              second as the digits change width. */}
          <span
            data-testid="voice-timer"
            style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
          >
            {formatDigits(`${mm}:${ss}`)}
          </span>
        </span>
      )}

      {/* ── THE WAIT ──────────────────────────────────────────────────────
          Everything after the microphone stops reports itself in this same
          slot: the transcription wait, and any failure of it.

          IT IS DELIBERATELY SHORT. The first version read «در حال تبدیل صدای
          شما به متن…» and, in شکایت اصلی's row, squeezed the field to about 40%
          of its width for the few seconds of the wait. */}
      {transcribing && (
        <span
          role="status"
          aria-live="polite"
          data-testid="voice-inline-status"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: C.textSecondary, whiteSpace: 'nowrap',
          }}
        >
          <Loader2 size={13} aria-hidden className="animate-spin" />
          {t('common.voice.inlineTranscribing')}
        </span>
      )}

      {/* Persists until the next press, which clears it (`start()` resets the
          error). No dismiss control: one more 44px target in a clinical form
          row costs more than a sentence that goes away when the dentist tries
          again. */}
      {!!error && !listening && (
        <span
          role="alert"
          data-testid="voice-inline-error"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.red }}
        >
          <AlertTriangle size={13} aria-hidden style={{ flexShrink: 0 }} />
          {t(`common.voice.error.${error}`)}
        </span>
      )}
    </>
  );
}
